import React from 'react';
import { useDispatch } from 'react-redux';

import { clearSelection, setCollapsedClonotypes, setSelectedSequences, toggleClonotypeCollapsed } from '../../store/slices/antibodyAnalysis.slice';
import type { AppDispatch } from '../../store/store';
import type { ClonotypeRow } from '../types/antibodyTable';

interface UseClonotypeSelectionParams {
  tableData: ClonotypeRow[];
  selectedSequences: Set<string>;
  collapsed: Record<string, boolean>;
}

/**
 * Encapsulates all clonotype expand/collapse and select/deselect logic for
 * OuterParentClonotypeTable, keeping the main component focused on rendering.
 *
 *
 */
export function useClonotypeSelection({ tableData, selectedSequences, collapsed }: UseClonotypeSelectionParams) {
  const dispatch = useDispatch<AppDispatch>();

  // ---- Selection predicates ----

  const isSequenceSelected = React.useCallback((cId: number, sId: string) => selectedSequences.has(`${cId}-${sId}`), [selectedSequences]);

  const isClonotypeSelected = React.useCallback(
    (row: ClonotypeRow) => row.sequences.some((seq) => selectedSequences.has(`${row.clonotypeId}-${seq.metadata.sequenceId}`)),
    [selectedSequences],
  );

  const isClonotypeAllSelected = React.useCallback(
    (row: ClonotypeRow) => row.sequences.length > 0 && row.sequences.every((seq) => selectedSequences.has(`${row.clonotypeId}-${seq.metadata.sequenceId}`)),
    [selectedSequences],
  );

  // ---- Derived selection state ----

  const allSelected = React.useMemo(() => tableData.length > 0 && tableData.every((row) => isClonotypeAllSelected(row)), [tableData, isClonotypeAllSelected]);

  const someSelected = React.useMemo(() => tableData.some((row) => isClonotypeSelected(row)), [tableData, isClonotypeSelected]);

  // ---- Expand / collapse  ----

  // Toggle a single row.
  const toggleExpanded = React.useCallback(
    (rowId: string) => {
      dispatch(toggleClonotypeCollapsed(rowId));
    },
    [dispatch],
  );

  // Expand all: clear the collapsed set entirely (absence = expanded).
  const handleExpandAll = React.useCallback(() => {
    dispatch(setCollapsedClonotypes({}));
  }, [dispatch]);

  // Expand selected: remove selected rows from the collapsed set.
  const handleExpandSelected = React.useCallback(() => {
    const next: Record<string, boolean> = { ...collapsed };
    for (const row of tableData) {
      if (isClonotypeSelected(row)) {
        delete next[`c_${row.clonotypeId}`];
      }
    }
    dispatch(setCollapsedClonotypes(next));
  }, [tableData, collapsed, isClonotypeSelected, dispatch]);

  // Collapse all: add every row to the collapsed set.
  const handleCollapseAll = React.useCallback(() => {
    const all: Record<string, boolean> = {};
    for (const row of tableData) {
      all[`c_${row.clonotypeId}`] = true;
    }
    dispatch(setCollapsedClonotypes(all));
  }, [tableData, dispatch]);

  // ---- Select all / deselect all  ----

  const handleSelectAll = React.useCallback(() => {
    if (allSelected) {
      dispatch(clearSelection());
      // Collapse all rows when deselecting everything
      const all: Record<string, boolean> = {};
      for (const row of tableData) {
        all[`c_${row.clonotypeId}`] = true;
      }
      dispatch(setCollapsedClonotypes(all));
    } else {
      const keys = tableData.flatMap((row) => row.sequences.map((seq) => `${row.clonotypeId}-${seq.metadata.sequenceId}`));
      dispatch(setSelectedSequences(keys));
      // Expand all rows when selecting everything
      dispatch(setCollapsedClonotypes({}));
    }
  }, [allSelected, tableData, dispatch]);

  return {
    // predicates
    isSequenceSelected,
    // derived state
    allSelected,
    someSelected,
    // expand/collapse
    toggleExpanded,
    handleExpandAll,
    handleExpandSelected,
    handleCollapseAll,
    // selection
    handleSelectAll,
  };
}
